import { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../store'
import { fetchFeed, appendFeed } from '../../store/slices/jobSlice'
import JobCard from '../../components/job/JobCard'
import ApplyModal from '../../components/referral/ApplyModal'
import { Job } from '../../types'
import { referralApi } from '../../api/referralApi'
import { jobApi } from '../../api/jobApi'
import { toast } from 'react-hot-toast'
import {
  Loader2, Search, Briefcase, Users,
  Wallet, Link2, TrendingUp, Sparkles,
} from 'lucide-react'

export default function FeedPage() {
  const dispatch      = useAppDispatch()
  const navigate      = useNavigate()
  const { feed, feedPage, feedHasMore, feedLoading } = useAppSelector((s) => s.jobs)
  const { user }      = useAppSelector((s) => s.auth)
  const { balance }   = useAppSelector((s) => s.wallet)
  const balanceRupees = (balance ?? 0) / 100

  const [applyJob,       setApplyJob]       = useState<Job | null>(null)
  const [searchQuery,    setSearchQuery]    = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  const avatarInitial = (user?.displayName || user?.email || 'U')[0].toUpperCase()

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 400)
    return () => clearTimeout(t)
  }, [searchQuery])

  useEffect(() => {
    dispatch(fetchFeed({ page: 0, search: debouncedQuery }))
  }, [dispatch, debouncedQuery])

  const handleScroll = useCallback(() => {
    const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 300
    if (nearBottom && feedHasMore && !feedLoading) {
      dispatch(appendFeed({ page: feedPage + 1, search: debouncedQuery }))
    }
  }, [dispatch, feedHasMore, feedLoading, feedPage, debouncedQuery])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const handleLike = async (jobId: number) => {
    try {
      await jobApi.toggleLike(jobId)
      dispatch(fetchFeed({ page: 0, search: debouncedQuery }))
    } catch {
      toast.error('Failed to update like')
    }
  }

  const handleApplySubmit = async (coverLetter: string, resumeFile: File | null) => {
    if (!applyJob) return
    try {
      const { data } = await referralApi.apply(applyJob.id, coverLetter)
      const application = data.data!
      if (resumeFile && application.resumeUploadUrl) {
        await fetch(application.resumeUploadUrl, {
          method: 'PUT',
          body: resumeFile,
          headers: { 'Content-Type': resumeFile.type },
        })
      }
      toast.success('Application submitted successfully!')
      setApplyJob(null)
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to submit application')
    }
  }

  const quickLinks = [
    { to: '/my-applications', label: 'My Applications', icon: Briefcase, color: '#8B5CF6' },
    { to: '/my-referrals',    label: 'My Referrals',    icon: Link2,     color: '#7C3AED' },
    { to: '/connections',     label: 'My Network',      icon: Users,     color: '#6D28D9' },
    { to: '/wallet',          label: 'Wallet',          icon: Wallet,    color: '#5B21B6' },
  ]

  const trendingSkills = ['React.js', 'Node.js', 'Python', 'AWS', 'TypeScript', 'Kubernetes', 'Go']

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[272px_1fr_272px] gap-5">

      {/* ── Left Sidebar ── */}
      <aside className="hidden lg:flex flex-col gap-4 self-start sticky top-20">

        {/* Profile mini-card */}
        <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid #EDE9FE', boxShadow: '0 2px 12px rgba(139,92,246,0.08)' }}>
          {/* Cover gradient */}
          <div className="h-16" style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 60%, #4C1D95 100%)' }} />

          <div className="px-4 pb-4">
            <div className="-mt-8 mb-2">
              <div className="w-16 h-16 rounded-full border-[3px] border-white flex items-center justify-center overflow-hidden" style={{ background: '#EDE9FE', boxShadow: '0 4px 14px rgba(139,92,246,0.25)' }}>
                {(user as any)?.profile?.profilePicUrl ? (
                  <img src={(user as any).profile.profilePicUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xl font-bold" style={{ color: '#7C3AED' }}>{avatarInitial}</span>
                )}
              </div>
            </div>

            <Link to="/profile" className="no-underline">
              <p className="font-semibold text-sm transition-colors" style={{ color: '#13082A' }}
                onMouseEnter={(e) => { (e.target as HTMLElement).style.color = '#7C3AED' }}
                onMouseLeave={(e) => { (e.target as HTMLElement).style.color = '#13082A' }}
              >
                {user?.displayName || user?.email}
              </p>
            </Link>
            <p className="text-xs mt-0.5" style={{ color: '#6B6080' }}>{user?.activeRole}</p>

            <div className="mt-3 pt-3 flex items-center justify-between" style={{ borderTop: '1px solid #EDE9FE' }}>
              <span className="text-xs" style={{ color: '#A89EC0' }}>Wallet Balance</span>
              <Link to="/wallet" className="text-xs font-semibold no-underline" style={{ color: '#7C3AED' }}>
                ₹{balanceRupees.toFixed(2)}
              </Link>
            </div>
          </div>
        </div>

        {/* Quick links */}
        <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid #EDE9FE', boxShadow: '0 2px 12px rgba(139,92,246,0.08)' }}>
          <p className="px-4 pt-4 pb-3 text-xs font-semibold uppercase tracking-widest" style={{ color: '#A89EC0' }}>
            Quick Links
          </p>
          {quickLinks.map(({ to, label, icon: Icon, color }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-3 px-4 py-3 text-sm no-underline transition-all duration-150"
              style={{ color: '#6B6080', borderTop: '1px solid #EDE9FE' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#F5F3FF'; (e.currentTarget as HTMLElement).style.color = '#13082A' }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = '#6B6080' }}
            >
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#EDE9FE' }}>
                <Icon size={14} style={{ color }} />
              </div>
              <span className="font-medium">{label}</span>
            </Link>
          ))}
        </div>
      </aside>

      {/* ── Center: Feed ── */}
      <div className="flex flex-col gap-4 min-w-0">

        {/* Search bar */}
        <div className="bg-white rounded-2xl p-4" style={{ border: '1px solid #EDE9FE', boxShadow: '0 2px 12px rgba(139,92,246,0.08)' }}>
          <div className="relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#A89EC0' }} />
            <input
              type="text"
              placeholder="Search jobs, companies, skills..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10"
            />
          </div>
        </div>

        {/* Sort label */}
        {feed.length > 0 && (
          <div className="flex items-center justify-between px-1">
            <p className="text-xs font-semibold" style={{ color: '#A89EC0' }}>
              {debouncedQuery ? `Results for "${debouncedQuery}"` : 'Latest Opportunities'}
            </p>
            <div className="flex items-center gap-1.5 text-xs" style={{ color: '#A89EC0' }}>
              <TrendingUp size={13} />
              <span>Sorted by latest</span>
            </div>
          </div>
        )}

        {/* Feed cards */}
        {feed.length === 0 && !feedLoading ? (
          <div className="bg-white rounded-2xl p-16 text-center" style={{ border: '1px solid #EDE9FE' }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#EDE9FE' }}>
              <Briefcase size={28} style={{ color: '#C4B5FD' }} />
            </div>
            <p className="text-base font-semibold" style={{ color: '#13082A' }}>No jobs found</p>
            <p className="text-sm mt-1" style={{ color: '#A89EC0' }}>Try adjusting your search or check back later</p>
          </div>
        ) : (
          feed.map((job) => (
            <JobCard key={job.id} job={job} onLike={handleLike} onApply={(j) => setApplyJob(j)} />
          ))
        )}

        {feedLoading && (
          <div className="flex justify-center py-8">
            <Loader2 size={26} className="animate-spin" style={{ color: '#8B5CF6' }} />
          </div>
        )}

        {!feedLoading && !feedHasMore && feed.length > 0 && (
          <p className="text-center text-xs py-6" style={{ color: '#A89EC0', borderTop: '1px solid #EDE9FE' }}>
            ✦ You've seen all available opportunities ✦
          </p>
        )}
      </div>

      {/* ── Right Sidebar ── */}
      <aside className="hidden lg:flex flex-col gap-4 self-start sticky top-20">

        {/* Trending skills */}
        <div className="bg-white rounded-2xl p-5" style={{ border: '1px solid #EDE9FE', boxShadow: '0 2px 12px rgba(139,92,246,0.08)' }}>
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-4" style={{ color: '#13082A' }}>
            <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: '#EDE9FE' }}>
              <TrendingUp size={13} style={{ color: '#7C3AED' }} />
            </div>
            Trending Skills
          </h3>
          <div className="space-y-2.5">
            {trendingSkills.map((skill, i) => (
              <div key={skill} className="flex items-center justify-between">
                <span className="text-sm font-medium" style={{ color: '#13082A' }}>{skill}</span>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={i < 3
                  ? { background: '#FEF3C7', color: '#92400E' }
                  : { background: '#D1FAE5', color: '#065F46' }
                }>
                  {i < 3 ? '🔥 Hot' : '↑ Rising'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Post a job CTA (referrers only) */}
        {user?.roles?.includes('REFERRER') && (
          <div className="rounded-2xl p-5 overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)', boxShadow: '0 6px 20px rgba(139,92,246,0.35)' }}>
            <div className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-10" style={{ background: 'white', transform: 'translate(30%, -30%)' }} />
            <div className="w-8 h-8 rounded-xl flex items-center justify-center mb-3" style={{ background: 'rgba(255,255,255,0.2)' }}>
              <Sparkles size={16} className="text-white" />
            </div>
            <h3 className="text-sm font-bold text-white mb-1">Post a Job</h3>
            <p className="text-xs text-white/75 leading-relaxed mb-4">
              Share openings and earn referral bonuses when candidates get hired.
            </p>
            <button
              onClick={() => navigate('/create-job')}
              className="w-full py-2 rounded-full text-sm font-bold transition-all"
              style={{ background: 'white', color: '#7C3AED' }}
              onMouseEnter={(e) => { (e.target as HTMLElement).style.background = '#EDE9FE' }}
              onMouseLeave={(e) => { (e.target as HTMLElement).style.background = 'white' }}
            >
              Post a Job
            </button>
          </div>
        )}

        {/* My jobs shortcut */}
        {user?.roles?.includes('REFERRER') && (
          <div className="bg-white rounded-2xl p-5" style={{ border: '1px solid #EDE9FE', boxShadow: '0 2px 12px rgba(139,92,246,0.08)' }}>
            <h3 className="text-sm font-semibold mb-2" style={{ color: '#13082A' }}>Your Postings</h3>
            <Link to="/my-jobs" className="text-sm font-semibold no-underline flex items-center gap-1" style={{ color: '#7C3AED' }}>
              View my jobs →
            </Link>
          </div>
        )}
      </aside>

      {applyJob && (
        <ApplyModal
          job={applyJob}
          onClose={() => setApplyJob(null)}
          onSubmit={handleApplySubmit}
        />
      )}
    </div>
  )
}
